import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, AlertCircle, Clock, Calendar } from 'lucide-react';
import { 
  getUnreadNotifications, 
  getAllNotifications,
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  getUnreadNotificationsCount,
  Notification 
} from '../lib/services/NotificationService';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface NotificationsDropdownProps {
  onClose?: () => void;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Charger les notifications
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = showAll 
        ? await getAllNotifications(50)
        : await getUnreadNotifications();

      if (error) {
        console.error('Erreur lors du chargement des notifications:', error);
      } else {
        setNotifications(data || []);
      }

      // Charger le compteur de notifications non lues
      const { count } = await getUnreadNotificationsCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Exception lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    
    // Recharger les notifications toutes les 30 secondes
    const interval = setInterval(loadNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [showAll]);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleMarkAsRead = async (notificationId: string) => {
    const { success } = await markNotificationAsRead(notificationId);
    if (success) {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Recharger le compteur
      const { count } = await getUnreadNotificationsCount();
      setUnreadCount(count);
    }
  };

  const handleMarkAllAsRead = async () => {
    const { success } = await markAllNotificationsAsRead();
    if (success) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  };

  const handleDelete = async (notificationId: string) => {
    const { success } = await deleteNotification(notificationId);
    if (success) {
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Recharger le compteur si c'était une notification non lue
      if (notification && !notification.is_read) {
        const { count } = await getUnreadNotificationsCount();
        setUnreadCount(count);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'periodic_transaction_ending':
        return <Calendar className="w-5 h-5 text-amber-500" />;
      case 'user_inactivity':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-primary-500" />;
    }
  };

  const formatNotificationDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Il y a moins d\'une heure';
      } else if (diffInHours < 24) {
        return `Il y a ${Math.floor(diffInHours)} heure${Math.floor(diffInHours) > 1 ? 's' : ''}`;
      } else if (diffInHours < 48) {
        return 'Hier';
      } else {
        return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
      }
    } catch (error) {
      return dateString;
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);
  const readNotifications = notifications.filter(n => n.is_read);

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50 max-h-[600px] flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-700">
        <div className="flex items-center space-x-2">
          <Bell className="w-5 h-5 text-white" />
          <h3 className="text-lg font-bold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="p-1.5 text-dark-400 hover:text-white hover:bg-dark-700 rounded transition-colors"
              title="Tout marquer comme lu"
            >
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-dark-400 hover:text-white hover:bg-dark-700 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-dark-700">
        <button
          onClick={() => setShowAll(false)}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            !showAll
              ? 'text-white border-b-2 border-primary-500'
              : 'text-dark-400 hover:text-white'
          }`}
        >
          Non lues ({unreadCount})
        </button>
        <button
          onClick={() => setShowAll(true)}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            showAll
              ? 'text-white border-b-2 border-primary-500'
              : 'text-dark-400 hover:text-white'
          }`}
        >
          Toutes
        </button>
      </div>

      {/* Notifications list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Bell className="w-12 h-12 text-dark-500 mb-4" />
            <p className="text-dark-400 text-center">
              {showAll ? 'Aucune notification' : 'Aucune notification non lue'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-dark-700">
            {/* Notifications non lues */}
            {!showAll && unreadNotifications.length > 0 && (
              <>
                {unreadNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 hover:bg-dark-700 transition-colors cursor-pointer"
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notification.message}</p>
                        <p className="text-dark-400 text-xs mt-1">
                          {formatNotificationDate(notification.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="p-1 text-dark-400 hover:text-white hover:bg-dark-600 rounded transition-colors"
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification.id);
                          }}
                          className="p-1 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Mode "Toutes" : afficher d'abord les non lues, puis les lues */}
            {showAll && (
              <>
                {/* Notifications non lues en premier */}
                {unreadNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 hover:bg-dark-700 transition-colors cursor-pointer bg-dark-800/50"
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notification.message}</p>
                        <p className="text-dark-400 text-xs mt-1">
                          {formatNotificationDate(notification.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="p-1 text-dark-400 hover:text-white hover:bg-dark-600 rounded transition-colors"
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification.id);
                          }}
                          className="p-1 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Séparateur si on a à la fois des lues et des non lues */}
                {unreadNotifications.length > 0 && readNotifications.length > 0 && (
                  <div className="px-4 py-2 border-t border-dark-700">
                    <p className="text-xs text-dark-500 font-medium">Notifications lues</p>
                  </div>
                )}
                
                {/* Notifications lues */}
                {readNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 hover:bg-dark-700 transition-colors opacity-75"
                  >
                    <div className="flex items-start space-x-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">{notification.message}</p>
                        <p className="text-dark-400 text-xs mt-1">
                          {formatNotificationDate(notification.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="p-1 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsDropdown;

